import { useEffect, useState, type ReactNode } from 'react'
import { FolderPlus } from 'lucide-react'
import { toast } from 'sonner'

import {
  readChatArtifactExportError,
  useExportChatArtifactMutation,
  type ChatArtifact,
  type Dashboard,
  type Doc
} from '@axilon-apps/domains-ontology-ui-state'

import FolderTreePicker from '../../components/explorer/FolderTreePicker'
import { readMutationError } from '../../components/explorer/mutation-error'
import { joinChildPath, type PathItem } from '../../components/explorer/tree'
import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'

export interface SaveArtifactDialogProps {
  open: boolean
  artifact: ChatArtifact
  /** Library the artifact is saved into, as named in the title and toasts. */
  library: 'Dashboards' | 'Documents'
  /** One saved item, as named in hints and conflict errors. */
  itemNoun: string
  description: string
  idPrefix: string
  folders: readonly PathItem[]
  items: readonly PathItem[]
  disabledPaths?: ReadonlySet<string>
  createFolder: (input: { name: string; path: string }) => Promise<{ path: string }>
  /** Saved item name for the Name field's value; its slug is the final path segment. */
  toItemName: (name: string) => string
  banner?: (submitting: boolean) => ReactNode
  onClose: () => void
  onSaved: (saved: Dashboard | Doc) => void
}

/** Folder picker, name, and path preview shared by the save-to-library dialogs. */
export default function SaveArtifactDialog({
  open,
  artifact,
  library,
  itemNoun,
  description,
  idPrefix,
  folders,
  items,
  disabledPaths,
  createFolder,
  toItemName,
  banner,
  onClose,
  onSaved
}: SaveArtifactDialogProps) {
  const [exportChatArtifact] = useExportChatArtifactMutation()
  const [parentPath, setParentPath] = useState('')
  const [newFolderOpen, setNewFolderOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [folderError, setFolderError] = useState<string | null>(null)
  const [name, setName] = useState(artifact.title)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setParentPath('')
    setNewFolderOpen(false)
    setNewFolderName('')
    setCreatingFolder(false)
    setFolderError(null)
    setName(artifact.title)
    setSubmitting(false)
    setError(null)
  }, [artifact.title, open])

  const itemName = toItemName(name)
  const previewPath = name.trim() === '' ? '' : joinChildPath(parentPath, itemName)

  const handleClose = () => {
    if (!submitting && !creatingFolder) onClose()
  }

  const closeNewFolder = () => {
    setNewFolderOpen(false)
    setNewFolderName('')
    setFolderError(null)
  }

  const handleCreateFolder = async () => {
    const folderPath = joinChildPath(parentPath, newFolderName)
    if (folderPath === '') {
      setFolderError('Folder name must contain at least one valid character.')
      return
    }
    setCreatingFolder(true)
    setFolderError(null)
    try {
      const folder = await createFolder({ name: newFolderName.trim(), path: folderPath })
      setParentPath(folder.path)
      setNewFolderOpen(false)
      setNewFolderName('')
    } catch (caught) {
      setFolderError(
        readMutationError(caught, `A folder or ${itemNoun} already exists at this path.`)
      )
    } finally {
      setCreatingFolder(false)
    }
  }

  const handleSubmit = async () => {
    if (previewPath === '') {
      setError('Name must contain at least one letter, digit, dash, underscore, or dot.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const saved = await exportChatArtifact({
        conversationId: artifact.conversation_id,
        artifactId: artifact.id,
        kind: artifact.kind ?? 'dashboard',
        name: itemName,
        path: previewPath
      }).unwrap()
      onSaved(saved)
      onClose()
    } catch (caught) {
      const failure = readChatArtifactExportError(caught)
      if (failure.kind === 'path_taken') {
        setError(`A ${itemNoun} already exists at /${previewPath}. Pick another folder or name.`)
      } else if (failure.kind === 'already_exported') {
        onClose()
        toast(`Already saved to ${library}`)
      } else {
        setError(failure.message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <DialogContent className="sm:max-w-120">
        <DialogHeader>
          <DialogTitle>Save to {library}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor={`${idPrefix}-folder`}>Folder</Label>
              {!newFolderOpen ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="h-6 px-1.5 text-xs"
                  disabled={submitting}
                  onClick={() => {
                    setNewFolderOpen(true)
                    setFolderError(null)
                  }}
                >
                  <FolderPlus aria-hidden />
                  New folder
                </Button>
              ) : null}
            </div>
            {newFolderOpen ? (
              <div className="flex items-center gap-2">
                <Input
                  aria-label="New folder name"
                  autoFocus
                  placeholder={`Create inside /${parentPath || '(root)'}`}
                  value={newFolderName}
                  disabled={creatingFolder}
                  onChange={(event) => setNewFolderName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      void handleCreateFolder()
                    }
                    if (event.key === 'Escape') closeNewFolder()
                  }}
                />
                <Button
                  size="sm"
                  disabled={creatingFolder || newFolderName.trim() === ''}
                  onClick={() => void handleCreateFolder()}
                >
                  {creatingFolder ? 'Creating…' : 'Create'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={creatingFolder}
                  onClick={closeNewFolder}
                >
                  Cancel
                </Button>
              </div>
            ) : null}
            {folderError ? (
              <div className="text-12 text-st-down" role="alert">
                {folderError}
              </div>
            ) : null}
            <FolderTreePicker
              id={`${idPrefix}-folder`}
              folders={folders}
              dashboards={items}
              value={parentPath}
              onChange={setParentPath}
              disabled={submitting || creatingFolder}
              disabledPaths={disabledPaths}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}-name`}>Name</Label>
            <Input
              id={`${idPrefix}-name`}
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={submitting}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void handleSubmit()
                }
              }}
            />
            <span className="text-11 text-ink-3">
              Used as the {itemNoun} name; its slug becomes the final path segment.
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-11 uppercase tracking-wider text-ink-3">Path</Label>
            <code className="rounded bg-bg-sunk px-2 py-1 font-mono text-12 text-ink-2">
              /{previewPath || '…'}
            </code>
          </div>
          {banner?.(submitting)}
          {error ? (
            <div className="rounded-md bg-st-down-bg p-2.5 text-12 text-st-down" role="alert">
              {error}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting || creatingFolder}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSubmit()}
            disabled={submitting || creatingFolder || name.trim() === ''}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
